import { Injectable } from '@angular/core';
import { DateTimeService } from '@examdojo/core/date-time';
import { mapToVoid } from '@examdojo/core/rxjs';
import { ErrorHandlerService } from '@examdojo/error-handling';
import kebabCase from 'lodash/kebabCase';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { finalize, map, Observable, of, switchMap, tap } from 'rxjs';
import { AssignmentBuilderQuery, AssignmentBuilderService } from '../assignment-builder';
import { ClassAssignmentHttpService } from './class-assignment-http.service';
import {
  ClassAssignmentHttpModel,
  ClassAssignmentStatus,
  ClassAssignmentStoreModel,
  CreateAssignmentData,
  DuplicateAssignmentData,
} from './class-assignment.model';
import { ClassAssignmentQuery } from './class-assignment.query';
import { ClassAssignmentStore } from './class-assignment.store';
import { LearningAssignmentHttpService } from './learning-assignment-http.service';

/** Fallback when no pace boundary matches; mirrors SQL `coalesce(..., 75)` in the teacher_assignments_list view. */
const DEFAULT_SECONDS_PER_MARK = 75;

@Injectable({ providedIn: 'root' })
export class ClassAssignmentService {
  constructor(
    private readonly store: ClassAssignmentStore,
    private readonly query: ClassAssignmentQuery,
    private readonly classAssignmentHttpService: ClassAssignmentHttpService,
    private readonly learningAssignmentHttp: LearningAssignmentHttpService,
    private readonly errorHandler: ErrorHandlerService,
    private readonly assignmentBuilderQuery: AssignmentBuilderQuery,
    private readonly assignmentBuilderService: AssignmentBuilderService,
    private readonly dateTimeService: DateTimeService,
  ) {}

  fetchWeeklyPublishMarksLimit(): Observable<void> {
    return this.classAssignmentHttpService.fetchWeeklyPublishMarksLimit().pipe(
      tap((limit) => {
        if (isNotNullish(limit)) {
          this.store.updateProp('weeklyPublishMarksLimit', limit);
        }
      }),
      mapToVoid(),
    );
  }

  init(): Observable<void> {
    this.store.setLoading(true);

    return this.classAssignmentHttpService.fetchAllAssignmentsView().pipe(
      this.errorHandler.setHttpErrorMetadata({ entity: 'class_assignment', action: 'fetch' }),
      map((assignments) => assignments.map((a) => this.mapToStoreModel(a))),
      tap((assignments) => {
        this.store.set(assignments);
      }),
      mapToVoid(),
      finalize(() => this.store.setLoading(false)),
    );
  }

  createAssignment(data: CreateAssignmentData) {
    return this.classAssignmentHttpService.createAssignment(data).pipe(
      this.errorHandler.setHttpErrorMetadata({ entity: 'class_assignment', action: 'create' }),
      switchMap((assignmentId) =>
        this.classAssignmentHttpService.fetchAssignment(assignmentId).pipe(
          map((assignment) => {
            const storeModel = this.mapToStoreModel(assignment);
            this.store.create(storeModel);
            return assignmentId;
          }),
        ),
      ),
    );
  }

  duplicateAssignment(data: DuplicateAssignmentData) {
    return this.classAssignmentHttpService.duplicateAssignment(data).pipe(
      this.errorHandler.setHttpErrorMetadata({ entity: 'class_assignment', action: 'create' }),
      switchMap((assignmentId) =>
        this.classAssignmentHttpService.fetchAssignment(assignmentId).pipe(
          map((assignment) => {
            const storeModel = this.mapToStoreModel(assignment);
            this.store.create(storeModel);
            return storeModel;
          }),
        ),
      ),
    );
  }

  deleteAssignment(assignmentId: string) {
    return this.classAssignmentHttpService.delete(assignmentId).pipe(
      this.errorHandler.setHttpErrorMetadata({ entity: 'class_assignment', action: 'delete' }),
      tap(() => this.store.delete(assignmentId)),
    );
  }

  fetchAssignmentStudents(assignmentId: string) {
    return this.classAssignmentHttpService
      .fetchAssignmentStudents(assignmentId)
      .pipe(this.errorHandler.setHttpErrorMetadata({ entity: 'class_assignment', action: 'fetch' }));
  }

  fetchTopicAnalysis(assignmentId: string) {
    return this.classAssignmentHttpService
      .fetchTopicAnalysis(assignmentId)
      .pipe(this.errorHandler.setHttpErrorMetadata({ entity: 'class_assignment', action: 'fetch' }));
  }

  fetchTeacherAutoGradedCount(): Observable<number> {
    return this.classAssignmentHttpService
      .fetchTeacherAutoGradedCount()
      .pipe(this.errorHandler.setHttpErrorMetadata({ entity: 'class_assignment', action: 'fetch' }));
  }

  refreshAssignment(assignmentId: string): Observable<ClassAssignmentStoreModel> {
    return this.classAssignmentHttpService.fetchAssignment(assignmentId).pipe(
      map((assignment) => this.mapToStoreModel(assignment)),
      tap((assignment) => this.store.upsert(assignment.id, assignment)),
    );
  }

  setActiveAssignment(id: string | null) {
    this.store.setActive(id);
  }

  publishAssignment(dueAt: Date) {
    const { activeAssignment, assignmentId } = this.getActiveAssignmentOrThrow();
    const totalMarks = this.assignmentBuilderQuery.getTotalMarks();

    const saveSectionsIfDirty$ = this.assignmentBuilderQuery.getProp('isDirty')
      ? this.assignmentBuilderService.saveDraft()
      : of(undefined);

    return saveSectionsIfDirty$.pipe(
      switchMap(() => this.classAssignmentHttpService.publishAssignment(assignmentId, dueAt)),
      this.errorHandler.setHttpErrorMetadata({ entity: 'class_assignment', action: 'update' }),
      tap(() =>
        this.store.update(activeAssignment.id, {
          status: 'PUBLISHED',
          is_published: true,
          due_at: dueAt.toISOString(),
          scheduled_at: new Date().toISOString(),
          total_marks: totalMarks,
          scheduled_at_draft: null,
        }),
      ),
      mapToVoid(),
    );
  }

  scheduleAssignment(scheduledAt: Date, dueAt: Date) {
    const { activeAssignment, assignmentId } = this.getActiveAssignmentOrThrow();
    const totalMarks = this.assignmentBuilderQuery.getTotalMarks();

    const saveSectionsIfDirty$ = this.assignmentBuilderQuery.getProp('isDirty')
      ? this.assignmentBuilderService.saveDraft()
      : of(undefined);

    return saveSectionsIfDirty$.pipe(
      switchMap(() => this.classAssignmentHttpService.scheduleAssignment(assignmentId, scheduledAt, dueAt)),
      this.errorHandler.setHttpErrorMetadata({ entity: 'class_assignment', action: 'update' }),
      tap(() =>
        this.store.update(activeAssignment.id, {
          status: 'SCHEDULED',
          scheduled_at: scheduledAt.toISOString(),
          due_at: dueAt.toISOString(),
          total_marks: totalMarks,
          scheduled_at_draft: null,
        }),
      ),
      mapToVoid(),
    );
  }

  saveDraftChanges(dueAt: Date | null, scheduledAtDraft: Date | null): Observable<void> {
    const { activeAssignment } = this.getActiveAssignmentOrThrow();
    const dueAtIso = dueAt?.toISOString() ?? null;

    return this.assignmentBuilderService.saveDraft(dueAt, scheduledAtDraft).pipe(
      this.errorHandler.setHttpErrorMetadata({ entity: 'class_assignment', action: 'update' }),
      tap(() =>
        this.store.update(activeAssignment.id, {
          due_at: dueAtIso,
          scheduled_at_draft: scheduledAtDraft?.toISOString() ?? null,
        }),
      ),
      mapToVoid(),
    );
  }

  updateTitle(name: string): Observable<void> {
    const { activeAssignment, assignmentId } = this.getActiveAssignmentOrThrow();

    return this.learningAssignmentHttp.update(assignmentId, { name }).pipe(
      this.errorHandler.setHttpErrorMetadata({ entity: 'class_assignment', action: 'update' }),
      tap(() => this.store.update(activeAssignment.id, { name })),
      mapToVoid(),
    );
  }

  updateDueDate(dueAt: Date | null): Observable<void> {
    const { activeAssignment } = this.getActiveAssignmentOrThrow();
    const dueAtIso = dueAt?.toISOString() ?? null;

    if (activeAssignment.due_at === dueAtIso) {
      return of(undefined);
    }

    return this.classAssignmentHttpService.update(activeAssignment.id, { due_at: dueAtIso }).pipe(
      this.errorHandler.setHttpErrorMetadata({ entity: 'class_assignment', action: 'update' }),
      tap(() => this.store.update(activeAssignment.id, { due_at: dueAtIso })),
      mapToVoid(),
    );
  }

  setActiveBySlug(slug: ClassAssignmentStoreModel['slug']): boolean {
    const assignment = this.query.getAllUIEntities().find((a) => a.slug === slug);
    if (!assignment) {
      return false;
    }
    this.store.setActive(assignment.id);
    return true;
  }

  private getActiveAssignmentOrThrow() {
    const activeAssignment = this.query.getActive();
    if (!activeAssignment?.assignment_id) {
      throw new Error('No active assignment found');
    }
    return { activeAssignment, assignmentId: activeAssignment.assignment_id };
  }

  private mapToStoreModel(httpModel: ClassAssignmentHttpModel): ClassAssignmentStoreModel {
    const id = httpModel.id ?? '';
    const name = httpModel.name ?? '';
    const className = httpModel.class_name ?? '';

    return {
      ...httpModel,
      id,
      assignment_id: httpModel.assignment_id ?? '',
      name,
      slug: `${kebabCase(`${className} ${name}`)}-${id.slice(0, 8)}`,
      status: (httpModel.status as ClassAssignmentStatus) ?? 'DRAFT',
      class_id: httpModel.class_id ?? '',
      class_name: httpModel.class_name ?? '',
      class_exam_first_date: this.dateTimeService.parseDateOnly(httpModel.class_exam_first_date),
      question_count: httpModel.question_count ?? 0,
      section_count: httpModel.section_count ?? 0,
      total_marks: httpModel.total_marks ?? 0,
      total_students: httpModel.total_students ?? 0,
      completed_count: httpModel.completed_count ?? 0,
      in_progress_count: httpModel.in_progress_count ?? 0,
      paused_count: httpModel.paused_count ?? 0,
      failed_count: httpModel.failed_count ?? 0,
      discarded_count: httpModel.discarded_count ?? 0,
      seconds_per_mark: httpModel.seconds_per_mark ?? DEFAULT_SECONDS_PER_MARK,
      has_unpublished_changes: httpModel.has_unpublished_changes ?? false,
      is_published: httpModel.is_published ?? false,
      created_at: httpModel.created_at ?? '',
      created_by: httpModel.created_by ?? '',
      updated_at: httpModel.updated_at ?? '',
    } as ClassAssignmentStoreModel;
  }
}
